import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { notification } from 'antd';
import { useAuth0 } from '@auth0/auth0-react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  addWorkspace,
  getUser,
  addProject,
  getProjectTemplates,
  inviteToWorkspace,
  generateWorkspaceInviteLink,
} from '../../../actions';
import { isValidEmail } from '../../../js/helper';
import { getMyOrgs } from '../../../js/functions';
import { logoutAction } from '../../../redux/general/actions';
import { bulkAddTasksAction } from '../../../redux/tasks/actions';
import AuthLayout from './AuthLayout';

const STEPS = [
  { id: 'name',    label: 'Name'    },
  { id: 'invite',  label: 'Invite'  },
  { id: 'project', label: 'Project' },
  { id: 'tasks',   label: 'Tasks'   },
];

// ── shared dark input ──────────────────────────────────────────────────────────
const DarkInput = ({ value, onChange, placeholder, error, onKeyDown, autoFocus, readOnly }) => (
  <div className="flex flex-col gap-1">
    <input
      autoFocus={autoFocus}
      readOnly={readOnly}
      value={value}
      onChange={onChange}
      onKeyDown={onKeyDown}
      placeholder={placeholder}
      className="w-full rounded-xl px-4 py-2.5 text-sm outline-none transition-all duration-150"
      style={{
        background: readOnly ? 'rgba(255,255,255,0.04)' : 'rgba(255,255,255,0.08)',
        border: `1px solid ${error ? 'rgba(248,113,113,0.5)' : 'rgba(255,255,255,0.14)'}`,
        color: 'rgba(255,255,255,0.9)',
      }}
    />
    {error && <p className="text-xs mt-0.5" style={{ color: 'rgba(248,113,113,0.9)' }}>{error}</p>}
  </div>
);

const DarkLabel = ({ children }) => (
  <label className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.65)' }}>{children}</label>
);

const DarkField = ({ label, children }) => (
  <div className="flex flex-col gap-1.5">
    <DarkLabel>{label}</DarkLabel>
    {children}
  </div>
);

// ── step indicator ─────────────────────────────────────────────────────────────
const StepIndicator = ({ current }) => (
  <div className="flex items-center justify-center gap-1.5 mb-6 sm:mb-8">
    {STEPS.map((step, i) => (
      <React.Fragment key={step.id}>
        <div className="flex flex-col items-center gap-1">
          <div
            className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all"
            style={
              i < current
                ? { background: 'rgba(99,102,241,0.9)', color: '#fff' }
                : i === current
                  ? { background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff', boxShadow: '0 0 0 3px rgba(99,102,241,0.25)' }
                  : { background: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.3)', border: '1px solid rgba(255,255,255,0.1)' }
            }
          >
            {i < current
              ? <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" /></svg>
              : i + 1
            }
          </div>
          <span className="text-[10px] font-medium" style={{ color: i === current ? 'rgba(165,180,252,0.9)' : 'rgba(255,255,255,0.25)' }}>
            {step.label}
          </span>
        </div>
        {i < STEPS.length - 1 && (
          <div className="flex-1 h-px mb-4 transition-colors" style={{ maxWidth: 32, background: i < current ? 'rgba(99,102,241,0.7)' : 'rgba(255,255,255,0.1)' }} />
        )}
      </React.Fragment>
    ))}
  </div>
);

// ── Step 1: Name ───────────────────────────────────────────────────────────────
const StepName = ({ workspaceTitle, setWorkspaceTitle, error, setError, onNext, orgs, selectedOrg, setSelectedOrg, isCreating }) => (
  <div className="flex flex-col gap-6">
    <div>
      <h2 className="text-xl font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.95)' }}>Name your workspace</h2>
      <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>A workspace holds your team's projects, tasks, and conversations.</p>
    </div>
    <DarkField label="Workspace name">
      <DarkInput
        autoFocus
        value={workspaceTitle}
        onChange={e => { setWorkspaceTitle(e.target.value); setError(''); }}
        onKeyDown={e => e.key === 'Enter' && onNext()}
        placeholder="e.g. Acme Corp, My Team…"
        error={error}
      />
    </DarkField>
    {orgs?.length > 0 && (
      <DarkField label="Organization">
        <div className="relative">
          <select
            value={selectedOrg?._id || ''}
            onChange={e => setSelectedOrg(orgs.find(o => o._id === e.target.value) || null)}
            className="w-full rounded-xl px-4 py-2.5 text-sm outline-none appearance-none cursor-pointer transition-all"
            style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.9)' }}
          >
            {orgs.map(org => (
              <option key={org._id} value={org._id} style={{ background: '#1e1b4b', color: '#fff' }}>
                {org.name || org.title || org._id}
              </option>
            ))}
          </select>
          <svg className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: 'rgba(255,255,255,0.35)' }}>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </DarkField>
    )}
    <button onClick={onNext} disabled={isCreating} className="w-full py-2.5 text-white text-sm font-semibold rounded-xl auth-btn-primary">
      {isCreating ? 'Creating…' : 'Continue'}
    </button>
  </div>
);

// ── Step 2: Invite ─────────────────────────────────────────────────────────────
const StepInvite = ({ workspaceId, onNext, onSkip }) => {
  const dispatch = useDispatch();
  const [tab, setTab] = useState('email');
  const [emailInput, setEmailInput] = useState('');
  const [emails, setEmails] = useState([]);
  const [emailError, setEmailError] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [inviteLink, setInviteLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [copied, setCopied] = useState(false);

  const addEmail = () => {
    const parts = emailInput.split(/[\s,]+/).filter(Boolean);
    const valid = [], invalid = [];
    parts.forEach(e => isValidEmail(e) ? valid.push(e) : invalid.push(e));
    if (invalid.length) { setEmailError(`Invalid: ${invalid.join(', ')}`); return; }
    if (!valid.length) return;
    setEmails(prev => [...new Set([...prev, ...valid])]);
    setEmailInput('');
    setEmailError('');
  };

  const sendInvites = async () => {
    if (!emails.length) { onNext(); return; }
    setIsSending(true);
    await dispatch(inviteToWorkspace({ workspaceId, invitees: emails.map(e => ({ email: e, role: 'member', userId: '' })) }));
    setIsSending(false);
    onNext();
  };

  const generateLink = () => {
    setIsGenerating(true);
    dispatch(generateWorkspaceInviteLink(workspaceId, { role: 'member', days: 7 }, (data) => {
      setIsGenerating(false);
      if (data?.token) setInviteLink(`${window.location.origin}/join/${data.token}`);
    }));
  };

  const copyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const tabStyle = (t) => tab === t
    ? { color: 'rgba(165,180,252,0.95)', borderBottom: '2px solid rgba(99,102,241,0.8)' }
    : { color: 'rgba(255,255,255,0.3)', borderBottom: '2px solid transparent' };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.95)' }}>Invite your team</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Add people to your workspace. You can always do this later.</p>
      </div>

      <div className="flex" style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        {['email', 'link'].map(t => (
          <button key={t} onClick={() => setTab(t)}
            className="px-4 py-2 text-sm font-medium -mb-px transition-colors"
            style={tabStyle(t)}
          >
            {t === 'email' ? 'Invite by email' : 'Invite with link'}
          </button>
        ))}
      </div>

      {tab === 'email' && (
        <div className="flex flex-col gap-3">
          <div className="flex gap-2">
            <input
              value={emailInput}
              onChange={e => { setEmailInput(e.target.value); setEmailError(''); }}
              onKeyDown={e => e.key === 'Enter' && addEmail()}
              placeholder="name@company.com…"
              className="flex-1 rounded-xl px-4 py-2.5 text-sm outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.9)' }}
            />
            <button onClick={addEmail}
              className="px-4 py-2.5 text-sm font-medium rounded-xl transition-colors"
              style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.7)' }}
            >
              Add
            </button>
          </div>
          {emailError && <p className="text-xs" style={{ color: 'rgba(248,113,113,0.9)' }}>{emailError}</p>}
          {emails.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {emails.map(e => (
                <span key={e} className="flex items-center gap-1 text-xs font-medium px-2.5 py-1 rounded-full"
                  style={{ background: 'rgba(99,102,241,0.2)', color: 'rgba(165,180,252,0.95)', border: '1px solid rgba(99,102,241,0.35)' }}>
                  {e}
                  <button onClick={() => setEmails(prev => prev.filter(x => x !== e))} className="ml-0.5" style={{ color: 'rgba(165,180,252,0.6)' }}>×</button>
                </span>
              ))}
            </div>
          )}
          <button onClick={sendInvites} disabled={isSending} className="w-full py-2.5 text-white text-sm font-semibold rounded-xl auth-btn-primary">
            {isSending ? 'Sending…' : emails.length > 0 ? `Send ${emails.length} invite${emails.length > 1 ? 's' : ''}` : 'Continue'}
          </button>
        </div>
      )}

      {tab === 'link' && (
        <div className="flex flex-col gap-3">
          <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Generate a shareable link — anyone with it can join your workspace.</p>
          {!inviteLink ? (
            <button onClick={generateLink} disabled={isGenerating}
              className="w-full py-2.5 text-sm font-medium rounded-xl transition-colors"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.13)', color: 'rgba(255,255,255,0.6)' }}
            >
              {isGenerating ? 'Generating…' : 'Generate invite link'}
            </button>
          ) : (
            <div className="flex gap-2">
              <input readOnly value={inviteLink}
                className="flex-1 rounded-xl px-4 py-2.5 text-sm outline-none"
                style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
              />
              <button onClick={copyLink}
                className="px-4 py-2.5 text-sm font-medium rounded-xl transition-colors"
                style={copied
                  ? { background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.35)', color: 'rgba(110,231,183,0.9)' }
                  : { background: 'rgba(255,255,255,0.09)', border: '1px solid rgba(255,255,255,0.14)', color: 'rgba(255,255,255,0.7)' }
                }
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          )}
          <button onClick={onNext} className="w-full py-2.5 text-white text-sm font-semibold rounded-xl auth-btn-primary">
            Continue
          </button>
        </div>
      )}

      <button onClick={onSkip} className="text-sm text-center transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
      >
        Skip for now
      </button>
    </div>
  );
};

// ── Step 3: Project ────────────────────────────────────────────────────────────
const StepProject = ({ workspaceId, onNext, onSkip, setCreatedProject }) => {
  const dispatch = useDispatch();
  const templates = useSelector(s => s.projectReducer.publicTemplates);
  const [name, setName] = useState('');
  const [nameError, setNameError] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  const handleCreate = async () => {
    if (!name.trim()) { setNameError('Project name is required'); return; }
    setIsCreating(true);
    const defaultTemplate = templates?.find(t => t?.default) || templates?.[0];
    await dispatch(addProject({
      title: name.trim(),
      workspace: workspaceId,
      template: defaultTemplate?._id,
      access: isPrivate ? 'private' : 'public',
    }, (project) => {
      setCreatedProject(project);
      onNext();
    }));
    setIsCreating(false);
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.95)' }}>Create your first project</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>Projects organize your work into boards and lists.</p>
      </div>
      <DarkField label="Project name">
        <DarkInput
          autoFocus
          value={name}
          onChange={e => { setName(e.target.value); setNameError(''); }}
          onKeyDown={e => e.key === 'Enter' && handleCreate()}
          placeholder="e.g. Marketing, Product Roadmap…"
          error={nameError}
        />
      </DarkField>
      <label className="flex items-center gap-2.5 cursor-pointer select-none">
        <div onClick={() => setIsPrivate(v => !v)}
          className="w-9 h-5 rounded-full relative transition-all"
          style={{ background: isPrivate ? 'rgba(99,102,241,0.8)' : 'rgba(255,255,255,0.12)' }}
        >
          <div className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${isPrivate ? 'translate-x-4' : 'translate-x-0.5'}`} />
        </div>
        <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>Private project</span>
      </label>
      <button onClick={handleCreate} disabled={isCreating} className="w-full py-2.5 text-white text-sm font-semibold rounded-xl auth-btn-primary">
        {isCreating ? 'Creating…' : 'Create project'}
      </button>
      <button onClick={onSkip} className="text-sm text-center transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
      >
        Skip for now
      </button>
    </div>
  );
};

// ── Step 4: Tasks ──────────────────────────────────────────────────────────────
const StepTasks = ({ workspaceId, project, onFinish }) => {
  const dispatch = useDispatch();
  const [tasks, setTasks] = useState(['', '', '']);
  const [isAdding, setIsAdding] = useState(false);
  const inputRefs = useRef([]);

  const updateTask = (i, val) => setTasks(prev => prev.map((t, idx) => idx === i ? val : t));
  const addRow = () => setTasks(prev => [...prev, '']);

  const handleFinish = async () => {
    const valid = tasks.map(t => t.trim()).filter(Boolean);
    if (valid.length) {
      setIsAdding(true);
      const projectId = project?._id;
      const sectionId = project?.sections?.[0]?._id || project?.sections?.[0] || '';
      const ws = workspaceId || sessionStorage.getItem('workspaceId') || localStorage.getItem('workspaceId');
      await dispatch(bulkAddTasksAction(valid.map(title => ({
        taskTitle: title, taskDescription: '', isCompleted: false, workspace: ws,
        ...(projectId ? { projects: [{ projectId, section: sectionId, position: 'top' }], addedTo: 'top' } : {}),
        dueDate: '',
      }))));
      setIsAdding(false);
    }
    onFinish();
  };

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h2 className="text-xl font-semibold mb-1" style={{ color: 'rgba(255,255,255,0.95)' }}>Add your first tasks</h2>
        <p className="text-sm" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {project?.title ? `Adding to "${project.title}"` : 'Start with a few tasks to hit the ground running.'}
        </p>
      </div>
      <div className="flex flex-col gap-2">
        {tasks.map((task, i) => (
          <div key={i} className="flex items-center gap-2">
            <div className="w-4 h-4 rounded flex-shrink-0" style={{ border: '1.5px solid rgba(255,255,255,0.2)' }} />
            <input
              ref={el => inputRefs.current[i] = el}
              value={task}
              onChange={e => updateTask(i, e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  if (i === tasks.length - 1) addRow();
                  else inputRefs.current[i + 1]?.focus();
                }
              }}
              placeholder={`Task ${i + 1}…`}
              className="flex-1 rounded-xl px-3 py-2 text-sm outline-none transition-all"
              style={{ background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)' }}
            />
            {tasks.length > 1 && (
              <button onClick={() => setTasks(prev => prev.filter((_, idx) => idx !== i))}
                className="text-lg leading-none transition-colors"
                style={{ color: 'rgba(255,255,255,0.2)' }}
                onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}
              >×</button>
            )}
          </div>
        ))}
        <button onClick={addRow} className="flex items-center gap-1.5 text-sm mt-1 transition-colors self-start"
          style={{ color: 'rgba(147,197,253,0.7)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(147,197,253,1)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(147,197,253,0.7)'}
        >
          <span className="text-lg leading-none">+</span> Add another task
        </button>
      </div>
      <button onClick={handleFinish} disabled={isAdding} className="w-full py-2.5 text-white text-sm font-semibold rounded-xl auth-btn-primary">
        {isAdding ? 'Saving…' : tasks.some(t => t.trim()) ? 'Create tasks & finish' : 'Finish setup'}
      </button>
      <button onClick={onFinish} className="text-sm text-center transition-colors" style={{ color: 'rgba(255,255,255,0.25)' }}
        onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
        onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.25)'}
      >
        Skip for now
      </button>
    </div>
  );
};

// ── main ───────────────────────────────────────────────────────────────────────
const CreateWorkspace = (props) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { logout: auth0Logout } = useAuth0();
  const user = useSelector(s => s.userReducer.user);
  const { pendingInvites } = props;

  const [step, setStep] = useState(0);
  const [workspaceTitle, setWorkspaceTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [orgs, setOrgs] = useState([]);
  const [selectedOrg, setSelectedOrg] = useState(null);
  const [createdWorkspace, setCreatedWorkspace] = useState(null);
  const [createdProject, setCreatedProject] = useState(null);

  useEffect(() => { dispatch(getUser()); dispatch(getProjectTemplates()); }, []);

  useEffect(() => {
    const myOrgs = getMyOrgs(user) || [];
    if (myOrgs.length > 0) {
      setOrgs(myOrgs);
      setSelectedOrg(prev => prev || myOrgs[0]);
    }
  }, [user?._id]);

  useEffect(() => {
    if (props.unauthorized) {
      ['token', 'workspaceId', 'organization', 'toggle', 'inviteUrl', 'Id'].forEach(k => localStorage.removeItem(k));
      ['workspaceId', 'organization'].forEach(k => sessionStorage.removeItem(k));
      dispatch(logoutAction());
      auth0Logout({ logoutParams: { returnTo: `${window.location.origin}/signin` } });
      window.location.href = '/signin';
    }
  }, [props.unauthorized]);

  const handleCreateWorkspace = async () => {
    if (!workspaceTitle.trim()) { setTitleError('Workspace name is required'); return; }
    if (isCreating) return;
    setIsCreating(true);
    const organizationId = user?.organizations?.length > 0 ? user.organizations[0]?._id : null;
    await dispatch(addWorkspace(
      { title: workspaceTitle.trim(), description: '', invitees: [], organizationId: selectedOrg?._id || organizationId },
      (ws) => {
        setIsCreating(false);
        if (ws?._id) {
          localStorage.setItem('workspaceId', ws._id);
          sessionStorage.setItem('workspaceId', ws._id);
          if (ws?.organization) {
            const orgId = ws.organization?._id || ws.organization;
            localStorage.setItem('organization', orgId);
            sessionStorage.setItem('organization', orgId);
          }
          setCreatedWorkspace(ws);
          setStep(1);
        }
      },
    ));
    setIsCreating(false);
  };

  const finish = () => navigate('/', { replace: true });

  return (
    <AuthLayout logoClickable>
      {step > 0 && (
        <style>{`.auth-skip-btn { position: fixed; top: 0; right: 0; z-index: 20; padding: 14px 32px; }`}</style>
      )}
      {step > 0 && (
        <div className="auth-skip-btn">
          <button onClick={finish} className="text-sm font-medium transition-colors"
            style={{ color: 'rgba(255,255,255,0.35)' }}
            onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.7)'}
            onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.35)'}
          >
            Skip setup
          </button>
        </div>
      )}

      <StepIndicator current={step} />

      {step === 0 && (
        <StepName
          workspaceTitle={workspaceTitle}
          setWorkspaceTitle={setWorkspaceTitle}
          error={titleError}
          setError={setTitleError}
          onNext={handleCreateWorkspace}
          orgs={orgs}
          selectedOrg={selectedOrg}
          setSelectedOrg={setSelectedOrg}
          isCreating={isCreating}
        />
      )}
      {step === 1 && createdWorkspace && (
        <StepInvite workspaceId={createdWorkspace._id} onNext={() => setStep(2)} onSkip={() => setStep(2)} />
      )}
      {step === 2 && createdWorkspace && (
        <StepProject workspaceId={createdWorkspace._id} onNext={() => setStep(3)} onSkip={() => setStep(3)} setCreatedProject={setCreatedProject} />
      )}
      {step === 3 && (
        <StepTasks workspaceId={createdWorkspace?._id} project={createdProject} onFinish={finish} />
      )}

      {step === 0 && pendingInvites?.length > 0 && (
        <button
          onClick={() => navigate('/select-workspace')}
          className="w-full mt-4 py-2.5 text-sm font-medium rounded-xl transition-colors"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.5)' }}
          onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.1)'}
          onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.06)'}
        >
          View workspace invites ({pendingInvites.length})
        </button>
      )}
    </AuthLayout>
  );
};

function mapStateToProps(state) {
  return {
    workspaces: state.workspaceReducer.getWorkspaces,
    unauthorized: state.userReducer.unauthorized,
    templates: state.projectReducer.publicTemplates,
    pendingInvites: state.workspaceReducer.pendingWorkspaceInvites,
  };
}

export default connect(mapStateToProps)(CreateWorkspace);
